import { Injectable } from "@angular/core";
import { CoreHttpClient, TwoFactorAuthMethod } from "@obg/ngx.core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { TwoFactorAuthConfigService } from "./two-factor-auth.config";
import { CommunicationChannel, OperationType, TwoFactorAuthMethodType } from "./two-factor-auth.const";
import {
	DeviceResponse,
	ForgetDeviceResponse,
	TwoFactorAuthEnabledMethods,
	TwoFactorAuthResponse,
	TwoFactorVerifyPayload,
	VerifyPasswordResponse,
} from "./two-factor-auth.model";

@Injectable({ providedIn: "root" })
export class TwoFactorAuthService {
	constructor(
		private client: CoreHttpClient,
		private config: TwoFactorAuthConfigService
	) {}

	postOneTimePasswordRequest(
		channel: CommunicationChannel,
		token: string | undefined,
		operationType = OperationType.TwoFactorVerification
	): Observable<TwoFactorAuthResponse> {
		return this.client.post({
			url: this.config.getOneTimePasswordRequestEndpoint(),
			data: {
				channel,
				token,
				operation: operationType,
			},
		});
	}

	patchOneTimePasswordRequest(otpDetails: TwoFactorVerifyPayload): Observable<TwoFactorAuthResponse> {
		const body: Partial<TwoFactorVerifyPayload> = { ...otpDetails };
		delete body?.loginToken;
		return this.client.patch({
			url: this.config.getOneTimePasswordRequestEndpoint(),
			data: {
				...body,
			},
		});
	}

	sendOnetimePassword(oneTimePassword: string): Observable<TwoFactorAuthResponse> {
		return this.client.patch({
			url: this.config.getSendOnetimePasswordEndpoint(),
			data: {
				token: oneTimePassword,
				twoFactorWithSms: true,
			},
		});
	}

	verifyUsersPassword(password: string): Observable<VerifyPasswordResponse> {
		return this.client.post({
			url: this.config.getVerifyUsersPasswordEndpoint(),
			data: {
				password,
			},
		});
	}

	operateAuthMethod(
		type: TwoFactorAuthMethodType,
		token?: string,
		methodName?: TwoFactorAuthMethod
	): Observable<TwoFactorAuthEnabledMethods[]> {
		const basePayload = {
			url: this.config.getOperateAuthMethodEndpoint(),
		};

		const createPayload = (method: TwoFactorAuthMethod) => ({
			...basePayload,
			data: {
				token,
				methodName: method,
			},
		});

		switch (type) {
			case TwoFactorAuthMethodType.Setup:
				return this.client.post(createPayload(methodName!));
			case TwoFactorAuthMethodType.MarkAsPrimary:
				return this.client.patch(createPayload(methodName!));
			case TwoFactorAuthMethodType.Remove:
				return this.client.delete(createPayload(methodName!));
			case TwoFactorAuthMethodType.RemoveAll:
				return this.client.delete({ url: this.config.getOperateAuthMethodAllEndpoint(), data: { token } });
			default:
				return this.client.get({ ...basePayload });
		}
	}

	checkCustomerDevice(deviceToken: string): Observable<boolean> {
		return this.client
			.get<DeviceResponse>({
				url: `${this.config.getDeviceEndpoint()}${deviceToken}`,
			})
			.pipe(map((response) => !!response.isRemembered));
	}

	forgetDevice(deviceId: string): Observable<ForgetDeviceResponse> {
		return this.client.delete({
			url: `${this.config.getDeviceEndpoint()}${deviceId}`,
		});
	}
}
